// Mutation Hooks
// Contains all mutation hooks for data modification operations

import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { useRef } from 'react'
import { useAppDispatch } from '../../store'
import { QUERY_KEYS } from '../queryKeys'
import { User, Report, MutationOptions } from '../types'
import { Team } from '../../../../pure-js/types/Team'
import { processHandlers } from '../processHandlers'
import { setQuestionnaire, updateQuestionnaireAnswer } from '../processHandlers/questionnaires'
import { BackendUserUpdate, QuestionnaireContext } from '../processHandlers/typeMappers'
import { Questionnaire } from '../../../../pure-js/types/Questionnaire'
import useAppState from '../../hooks/useAppState'
import { TrainingSprintConfig } from '../../features/facilitator/facilitatorTrainingSprintCreation/types'
import type { FacilitatorGameCallLinkRequest } from '../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import type { SprintActivityWherebyMeetingRequest } from '../../../../pure-js/types/WherebyTypes'
import type {
  EndSprintActivityForTeamRequest,
  SendTeamToActivityRequest,
  SprintLaunchOptions
} from '../../../../pure-js/types/Sprint'
import { addPendingReportId } from '../../store/slices/notificationsSlice'
import { markViewedLocally, markAllViewedLocally } from '../../store/slices/inAppNotificationsSlice'
import type {
  ArchiveReflectionRequest,
  CreateReflectionRequest,
  DeleteReflectionRequest,
  GenerateReflectionDataRequest,
  Reflection,
  SendReflectionCommitmentsRequest,
  UpdateReflectionRequest
} from '../../../../pure-js/types/Reflection'
import type { AICoachPart } from '../../../../pure-js/types/AICoach'
import type { ClaimWherebyCallRequest, ReleaseWherebyCallRequest } from '../../../../pure-js/types/WherebyTypes'
import type { ApiResponse } from '../types'
import type { AppAssistantRequest } from '../../../../pure-js/types/AppAssistant'
import type { CreateOrganizationRequest, Organization } from '../../../../pure-js/types/types'
import type { OrgRole, UpdateOrgMemberRequest } from '../../../../pure-js/types/OrganizationMembership'
import type { CompleteOrganizationOnboardingPayload } from '../processHandlers/organizations'
import { auth } from '../../libs/Firebase'
import type { OrgSwitchHandlers } from '../../features/organization/orgSwitch/OrgSwitchContext'

/** `invalidateQueries({ queryKey: ['reflections'], exact: false })` also matches `['reflections','detail',id]` → duplicate getReflection per update. Only refresh list queries. */
function invalidateReflectionListQueriesOnly(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    predicate: (query) =>
      Array.isArray(query.queryKey) &&
      query.queryKey[0] === QUERY_KEYS.REFLECTIONS_PREFIX &&
      query.queryKey[1] !== QUERY_KEYS.REFLECTION_DETAIL_SEGMENT,
    refetchType: 'active'
  })
}

export const useAskAppAssistant = (options?: MutationOptions) =>
  useMutation({
    mutationFn: (request: AppAssistantRequest) => processHandlers.askAppAssistant(request),
    onSuccess: (data) => {
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })

// Tracks the user's active org id in a ref kept in sync with state. Reflection mutations
// hold this ref so their `onSuccess` can resolve the org id at execution time — not at
// hook-render time — and therefore never write the response under the previous org's key
// after a user switches orgs mid-mutation (e.g. during a long generateReflectionData).
function useLiveQueryOrgIdRef() {
  const { state } = useAppState()
  const queryOrgId = state.user?.activeOrganizationId ?? state.user?.organizations?.[0]?.organizationId
  const ref = useRef<string | undefined>(queryOrgId)
  ref.current = queryOrgId
  return ref
}

// User Mutations
export const useCreateUser = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (userData: Partial<User>) => processHandlers.createUser(userData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useUpdateUser = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, userData }: { id: string; userData: Partial<User> | BackendUserUpdate }) =>
      processHandlers.updateUser(id, userData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USER_DETAIL, variables.id] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useDeleteUser = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => processHandlers.deleteUser(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] })
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.USER_DETAIL, id] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

// Session Mutations (removed unused stubs)

// Team Mutations
export const useCreateTeam = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (teamData: Partial<Team>) => processHandlers.createTeam(teamData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAMS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useUpdateTeam = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, teamData }: { id: string; teamData: Partial<Team> }) => processHandlers.updateTeam(id, teamData),
    onSuccess: (data, _variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAM_DETAIL_PREFIX] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAMS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useDeleteTeam = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => processHandlers.deleteTeam(id),
    onSuccess: (data, _id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAMS] })
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.TEAM_DETAIL_PREFIX] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useAddTeamMember = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      processHandlers.addTeamMember(teamId, userId),
    onSuccess: (data, _variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAM_DETAIL_PREFIX] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAMS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useRemoveTeamMember = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      processHandlers.removeTeamMember(teamId, userId),
    onSuccess: (data, _variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAM_DETAIL_PREFIX] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAMS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

// Report Mutations
export const useCreateReport = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (reportData: Partial<Report>) => processHandlers.createReport(reportData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REPORTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GENERATED_REPORTS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useUpdateReport = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: ({ id, reportData }: { id: string; reportData: Partial<Report> }) =>
      processHandlers.updateReport(id, reportData, state),
    onSuccess: (data, _variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REPORT_DETAIL_PREFIX] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REPORTS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useFinalizeReport = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (id: string) => processHandlers.finalizeReport(id, state),
    onSuccess: (data, _id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REPORT_DETAIL_PREFIX] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REPORTS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useDeleteReport = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (id: string) => processHandlers.deleteReport(id, state),
    onSuccess: async (data) => {
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.REPORT_DETAIL_PREFIX] })
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.REPORTS],
        refetchType: 'active'
      })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GENERATED_REPORTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FACILITATOR_DASHBOARD] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useGenerateReport = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sessionId: string) => processHandlers.generateReport(sessionId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.REPORTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.GENERATED_REPORTS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

// Generate report data for report flow (creates/updates a draft Report with datapoints)
export const useGenerateReportDataMutation = (options?: MutationOptions) => {
  const { state } = useAppState()
  const dispatch = useAppDispatch()

  return useMutation({
    mutationFn: (request: {
      sessions: { id: string; teamId: string }[]
      reportId?: string
      title?: string
      sprintIds?: string[]
    }) => processHandlers.generateReportData(request, state),
    onSuccess: (data) => {
      if (data?.data?.id) {
        dispatch(addPendingReportId(data.data.id))
      }
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

// Training Sprint Mutations
export const useCreateTrainingSprint = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sprintData: Record<string, unknown>) => processHandlers.createTrainingSprint(sprintData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_TRAINING_SPRINTS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useUpdateTrainingSprint = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, sprintData }: { id: string; sprintData: Record<string, unknown> }) =>
      processHandlers.updateTrainingSprint(id, sprintData),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINT_DETAIL, variables.id] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINTS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useDeleteTrainingSprint = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => processHandlers.deleteTrainingSprint(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINTS] })
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINT_DETAIL, id] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

// Sprint Mutations (Firebase Backend) - Accept frontend TrainingSprintConfig
// These handle transformation internally in processHandlers
export const useCreateDraftSprint = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (sprintConfig: TrainingSprintConfig) =>
      processHandlers.createDraftSprintFromConfig(sprintConfig, state),
    onSuccess: (data) => {
      // Invalidate all sprint-related queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SPRINTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVE_TRAINING_SPRINTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_TRAINING_SPRINTS] })
      // Invalidate dashboard to refresh sprint counts and lists
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FACILITATOR_DASHBOARD] })
      // Invalidate upcoming activities queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER_LIST] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useUpdateSprint = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: ({
      sprintId,
      sprintConfig,
      launch
    }: {
      sprintId: string
      sprintConfig: TrainingSprintConfig
      launch?: SprintLaunchOptions
    }) => processHandlers.updateSprintFromConfig(sprintId, sprintConfig, state, launch),
    onSuccess: (data, variables) => {
      // Invalidate specific sprint and lists
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SPRINT, variables.sprintId] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SPRINTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINT_DETAIL, variables.sprintId] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVE_TRAINING_SPRINTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_TRAINING_SPRINTS] })
      // Invalidate dashboard to refresh sprint counts and lists
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FACILITATOR_DASHBOARD] })
      // Invalidate upcoming activities queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER_LIST] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

/** The launch is a job: the sprint turns ACTIVE only when the job ends, so this runs then, not when the request returns. */
export const invalidateSprintQueriesAfterLaunch = (queryClient: QueryClient, sprintId: string) => {
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SPRINT, sprintId] })
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SPRINTS] })
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINTS] })
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINT_DETAIL, sprintId] })
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVE_TRAINING_SPRINTS] })
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_TRAINING_SPRINTS] })
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FACILITATOR_DASHBOARD] })
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES] })
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER] })
  queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_ACTIVITIES_GAMER_LIST] })
}

export const useDeleteSprint = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (sprintId: string) => processHandlers.deleteSprint(sprintId, state),
    onSuccess: (data, sprintId) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SPRINT, sprintId] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SPRINTS] })
      // Invalidate all training sprints queries (including those with filters for the management page)
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINT_DETAIL, sprintId] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVE_TRAINING_SPRINTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.UPCOMING_TRAINING_SPRINTS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FACILITATOR_DASHBOARD] })
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.SPRINT, sprintId] })
      queryClient.removeQueries({ queryKey: [QUERY_KEYS.TRAINING_SPRINT_DETAIL, sprintId] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

// Batch Operations
export const useBatchCreateUsers = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (usersData: Partial<User>[]) => processHandlers.batchCreateUsers(usersData),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.USERS] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

// Additional Mutations for Phase 3 Migration

const WHEREBY_CONVERGENCE_REFETCH_DELAY_MS = 5000

export const useCreateWherebyMeeting = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (meetingData: { roomNamePrefix?: string; endDate?: string; teamId: string }) =>
      processHandlers.createWherebyMeeting(meetingData, state),
    onSuccess: (data) => {
      // Refresh the team's active room (whereby_meetings) and any session views.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SESSIONS] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAM_WHEREBY_MEETING] })

      // One delayed refetch so that if teammates created rooms at the same instant, every
      // client picks up the active room and converges on it (no ongoing polling).
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAM_WHEREBY_MEETING] })
      }, WHEREBY_CONVERGENCE_REFETCH_DELAY_MS)

      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useEnsureSprintActivityWherebyMeeting = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (params: SprintActivityWherebyMeetingRequest) =>
      processHandlers.ensureSprintActivityWherebyMeeting(params, state),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.SPRINT_ACTIVITY_WHEREBY_MEETING] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useEnsureActivityWherebyMeeting = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (params: { teamId: string; sprintId: string; activityId: string }) =>
      processHandlers.ensureActivityWherebyMeeting(params, state),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVITY_WHEREBY_MEETING] })
      // One delayed refetch so concurrent joiners converge on the same activity room.
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ACTIVITY_WHEREBY_MEETING] })
      }, WHEREBY_CONVERGENCE_REFETCH_DELAY_MS)

      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

// One live Whereby call per user. Nothing to invalidate — `useWherebyCallOwnership` drives the
// connection off the response and watches the ownership doc for live changes.
export const useClaimWherebyCall = () => {
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: ClaimWherebyCallRequest) => processHandlers.claimWherebyCall(req, state)
  })
}

export const useReleaseWherebyCall = () => {
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: ReleaseWherebyCallRequest) => processHandlers.releaseWherebyCall(req, state)
  })
}

// Questionnaire Mutations

export const useSetQuestionnaire = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { userId: string; context: QuestionnaireContext | string }) =>
      setQuestionnaire(params as { userId: string; context: QuestionnaireContext }),
    onSuccess: (data, variables) => {
      // Invalidate specific questionnaire using composite key
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.QUESTIONNAIRE, JSON.stringify({ userId: variables.userId, context: variables.context })]
      })
      // Also invalidate all questionnaires for this user
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUESTIONNAIRE] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useClearQuestionnaire = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: { userId: string; context: QuestionnaireContext | string }) =>
      processHandlers.clearQuestionnaire(params),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.QUESTIONNAIRE, JSON.stringify({ userId: variables.userId, context: variables.context })]
      })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUESTIONNAIRE] })
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUESTIONNAIRE_ANSWER] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useUpdateQuestionnaireAnswer = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      questionnaire: Questionnaire
      questionId: string
      userId: string
      answer: string
      slotId?: string
      textKeyName?: string
    }) => updateQuestionnaireAnswer(params),
    onSuccess: (data, variables) => {
      // Invalidate specific answer using composite key
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.QUESTIONNAIRE_ANSWER,
          JSON.stringify({ questionnaireId: variables.questionnaire.id, questionId: variables.questionId })
        ]
      })
      // Also invalidate all answers
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.QUESTIONNAIRE_ANSWER] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useTriggerAICoach = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: async ({ sessionId, part }: { sessionId: string; part: AICoachPart }) => {
      const response = await processHandlers.triggerAICoach(sessionId, part, state)
      if (!response.success || !response.data.success) {
        throw new Error(response.error ?? response.data.error ?? 'Failed to trigger AI Coach')
      }
      return response
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AI_COACH_SESSION] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useReplayAICoach = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: async ({ sessionId }: { sessionId: string }) => {
      const response = await processHandlers.replayAICoach(sessionId, state)
      if (!response.success || !response.data.success) {
        throw new Error(response.error ?? response.data.error ?? 'Failed to replay AI Coach')
      }
      return response
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.AI_COACH_SESSION] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

// In-App Notification Mutations

// Reflection Mutations

export const useCreateReflection = (options?: MutationOptions) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (req: CreateReflectionRequest) => processHandlers.createReflection(req),
    onSuccess: (data) => {
      invalidateReflectionListQueriesOnly(queryClient)
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useUpdateReflection = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const queryOrgIdRef = useLiveQueryOrgIdRef()

  return useMutation({
    mutationFn: (req: UpdateReflectionRequest) => processHandlers.updateReflection(req),
    onSuccess: (apiResponse, variables) => {
      const id = variables.reflectionId
      const queryOrgId = queryOrgIdRef.current
      if (apiResponse?.success && apiResponse.data) {
        queryClient.setQueryData(QUERY_KEYS.REFLECTION_DETAIL(queryOrgId, id), {
          data: apiResponse.data,
          success: true
        })
      } else if (apiResponse?.success) {
        // Mocks / edge responses may omit `data`; refresh once without double-invalidating via list predicate.
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.REFLECTION_DETAIL(queryOrgId, id), refetchType: 'active' })
      }
      invalidateReflectionListQueriesOnly(queryClient)
      options?.onSuccess?.(apiResponse)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useArchiveReflection = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const queryOrgIdRef = useLiveQueryOrgIdRef()

  return useMutation({
    mutationFn: (req: ArchiveReflectionRequest) => processHandlers.archiveReflection(req),
    onSuccess: (apiResponse, variables) => {
      const id = variables.reflectionId
      const queryOrgId = queryOrgIdRef.current
      if (apiResponse?.success && apiResponse.data) {
        queryClient.setQueryData(QUERY_KEYS.REFLECTION_DETAIL(queryOrgId, id), {
          data: apiResponse.data,
          success: true
        })
      } else if (apiResponse?.success) {
        queryClient.invalidateQueries({ queryKey: QUERY_KEYS.REFLECTION_DETAIL(queryOrgId, id), refetchType: 'active' })
      }
      invalidateReflectionListQueriesOnly(queryClient)
      options?.onSuccess?.(apiResponse)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useDeleteReflection = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const queryOrgIdRef = useLiveQueryOrgIdRef()

  return useMutation({
    mutationFn: (req: DeleteReflectionRequest) => processHandlers.deleteReflection(req),
    onSuccess: (data, variables) => {
      queryClient.removeQueries({
        queryKey: QUERY_KEYS.REFLECTION_DETAIL(queryOrgIdRef.current, variables.reflectionId)
      })
      invalidateReflectionListQueriesOnly(queryClient)
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useGenerateReflectionData = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const queryOrgIdRef = useLiveQueryOrgIdRef()

  return useMutation({
    mutationFn: (req: GenerateReflectionDataRequest) => processHandlers.generateReflectionData(req),
    onSuccess: (apiResponse, variables) => {
      const id = variables.reflectionId
      if (apiResponse?.success && apiResponse.data) {
        const key = QUERY_KEYS.REFLECTION_DETAIL(queryOrgIdRef.current, id)
        const old = queryClient.getQueryData<ApiResponse<Reflection | undefined>>(key)
        if (old?.data) {
          queryClient.setQueryData(key, {
            ...old,
            data: {
              ...old.data,
              communication_balance_data:
                apiResponse.data.communication_balance_data ?? old.data.communication_balance_data,
              communication_balance_sessions:
                apiResponse.data.communication_balance_sessions ?? old.data.communication_balance_sessions,
              psych_safety_data: apiResponse.data.psych_safety_data ?? old.data.psych_safety_data
            }
          })
        } else {
          queryClient.invalidateQueries({ queryKey: key, refetchType: 'active' })
        }
      }
      options?.onSuccess?.(apiResponse)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useSendReflectionCommitments = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const queryOrgIdRef = useLiveQueryOrgIdRef()

  return useMutation({
    mutationFn: (req: SendReflectionCommitmentsRequest) => processHandlers.sendReflectionCommitments(req),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.REFLECTION_DETAIL(queryOrgIdRef.current, variables.reflectionId),
        refetchType: 'active'
      })
      invalidateReflectionListQueriesOnly(queryClient)
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

// Organization mutations (multi-tenant)

export type SetActiveOrganizationVariables = {
  organizationId: string
  targetLabel?: string
}

export type SetActiveOrganizationOptions = MutationOptions & {
  /** Skip org-switch overlay (e.g. login bootstrap — not for sidebar switcher). */
  silent?: boolean
  /** Handlers from `useOrgSwitchHandlers()` — required for overlay unless `silent`. */
  orgSwitch?: OrgSwitchHandlers
}

export const useSetActiveOrganization = (options?: SetActiveOrganizationOptions) => {
  const queryClient = useQueryClient()
  const { state, setState } = useAppState()
  const { silent, orgSwitch } = options ?? {}

  return useMutation({
    mutationFn: async ({ organizationId }: SetActiveOrganizationVariables) => {
      const res = await processHandlers.setActiveOrganization(state, { organizationId })
      if (!res.success || !res.data?.activeOrganizationId) {
        throw new Error(res.error || 'set-active-organization-failed')
      }
      const claims = await processHandlers.refreshAuthClaimsForLoggedInUser(state)
      if (!claims.success) {
        throw new Error(claims.error || 'refresh-claims-failed')
      }
      const firebaseUser = auth.currentUser
      if (firebaseUser) {
        await firebaseUser.getIdToken(true)
      }
      return organizationId
    },
    onMutate: async ({ organizationId, targetLabel }) => {
      if (!silent && orgSwitch) {
        orgSwitch.beginSwitch(organizationId, targetLabel)
      }
      await queryClient.cancelQueries()
    },
    onSuccess: (organizationId) => {
      setState((prev) => ({
        ...prev,
        user: { ...prev.user, activeOrganizationId: organizationId }
      }))
      // Wipe the cache synchronously — invalidateQueries only schedules background
      // refetches, so subscribers reading queries whose key embeds orgId as a discriminator
      // (not as a discriminating part of the key prefix) would briefly render previous-org
      // data after the new-org render. Clear evicts everything; queries re-fetch fresh.
      queryClient.clear()
      if (!silent && orgSwitch) {
        orgSwitch.enterDataPhase(organizationId)
      }
      options?.onSuccess?.(organizationId)
    },
    onError: (error) => {
      if (!silent && orgSwitch) {
        orgSwitch.failSwitch()
      }
      options?.onError?.(error)
    },
    onSettled: options?.onSettled
  })
}

export const useCreateOrganization = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: CreateOrganizationRequest) => processHandlers.createOrganization(state, req),
    onSuccess: (res) => {
      if (res.success) {
        void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS_DIRECTORY] })
      }
      options?.onSuccess?.(res)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useUpdateOrganization = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: { organizationId: string; payload: Partial<Organization> }) =>
      processHandlers.updateOrganization(state, req),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATION_DETAIL(variables.organizationId) })
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS_DIRECTORY] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useInviteUserToOrganization = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: { organizationId: string; email: string; role: OrgRole }) =>
      processHandlers.inviteUserToOrganization(state, req),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATION_INVITES(variables.organizationId) })
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORG_MEMBERS(variables.organizationId) })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useCreateOrgMemberByEmail = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: { organizationId: string; email: string; role: OrgRole }) =>
      processHandlers.createOrgMemberByEmail(state, req),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORG_MEMBERS(variables.organizationId) })
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATION_DETAIL(variables.organizationId) })
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATION_INVITES(variables.organizationId) })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useRevokeOrgInvite = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: { organizationId: string; inviteId: string }) =>
      processHandlers.revokeOrgInvite(state, req.inviteId),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATION_INVITES(variables.organizationId) })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useResendOrgInvite = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: { organizationId: string; inviteId: string }) =>
      processHandlers.resendOrgInvite(state, req.inviteId),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATION_INVITES(variables.organizationId) })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useUpdateOrgMember = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: UpdateOrgMemberRequest) => processHandlers.updateOrgMember(state, req),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORG_MEMBERS(variables.organizationId) })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useRemoveUserFromOrganization = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: { organizationId: string; userId: string }) =>
      processHandlers.removeUserFromOrganization(state, req),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORG_MEMBERS(variables.organizationId) })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useAddMemberToOrganization = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: { organizationId: string; userId: string; role: OrgRole }) =>
      processHandlers.addMemberToOrganization(state, req),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORG_MEMBERS(variables.organizationId) })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

/**
 * Finalize organization onboarding (flips status `PENDING_ONBOARDING` → `ACTIVE`).
 * Used by the wizard at `/org/:orgId/onboarding`. Invalidates the specific org's
 * detail query (so the wizard guard can re-evaluate) and the super-admin org
 * directory listing.
 */
export const useCompleteOrganizationOnboarding = (options?: MutationOptions) => {
  const queryClient = useQueryClient()
  const { state } = useAppState()

  return useMutation({
    mutationFn: (req: { organizationId: string; payload: CompleteOrganizationOnboardingPayload }) =>
      processHandlers.completeOrganizationOnboarding(state, req),
    onSuccess: (data, variables) => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEYS.ORGANIZATION_DETAIL(variables.organizationId) })
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ORGANIZATIONS_DIRECTORY] })
      options?.onSuccess?.(data)
    },
    onError: options?.onError,
    onSettled: options?.onSettled
  })
}

export const useMarkNotificationViewed = () => {
  const dispatch = useAppDispatch()
  return useMutation({
    mutationFn: (notificationId: string) => processHandlers.markNotificationViewed(notificationId),
    onMutate: (notificationId) => {
      dispatch(markViewedLocally(notificationId))
    }
  })
}

export const useMarkAllNotificationsViewed = () => {
  const dispatch = useAppDispatch()
  return useMutation({
    mutationFn: () => processHandlers.markAllNotificationsViewed(),
    onMutate: () => {
      dispatch(markAllViewedLocally())
    }
  })
}

// Nothing to invalidate: the link is opened in a new tab and the tower keeps polling on its own.
export const useFacilitatorGameCallLink = () =>
  useMutation({
    mutationFn: (request: FacilitatorGameCallLinkRequest) => processHandlers.getFacilitatorGameCallLink(request)
  })

// Nothing to invalidate: the link is opened in a new tab and the tower keeps polling on its own.
export const useFacilitatorSprintCallLink = () =>
  useMutation({
    mutationFn: (request: SprintActivityWherebyMeetingRequest) => processHandlers.getFacilitatorSprintCallLink(request)
  })

// Nothing to invalidate: the team's pages move on their own, and the tower keeps polling.
export const useSendTeamToActivity = () =>
  useMutation({
    mutationFn: (request: SendTeamToActivityRequest) => processHandlers.sendTeamToActivity(request)
  })

// The tower polls every few seconds; invalidating takes the team's card off it at once rather than on the next tick.
export const useEndSprintActivityForTeam = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (request: EndSprintActivityForTeamRequest) => processHandlers.endSprintActivityForTeam(request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FACILITATOR_LIVE_SESSION_MONITOR] })
    }
  })
}
